import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../store/useStore";
import { buildAssetUrl } from "../../utils/api";
import useBodyScrollLock from "../../utils/useBodyScrollLock";
import { getProductPath } from "../../utils/productSlug";
import { FaSearch, FaClock, FaFire } from "react-icons/fa";

const getProductId = (p) => p.product_id ?? p.id;
const getProductName = (p) => p.name ?? p.product_name ?? "Product";
const getLocalizedNames = (p, name) =>
  [p.product_name_hindi, p.product_name_marathi]
    .filter((localizedName) => localizedName && localizedName !== name)
    .filter(
      (localizedName, index, names) => names.indexOf(localizedName) === index,
    );

const ResultList = ({ results, searchQuery, onSelect, onViewAll }) => (
  <>
    {results.map((product) => {
      const id = getProductId(product);
      const name = getProductName(product);
      const localizedNames = getLocalizedNames(product, name);
      return (
        <button
          key={id}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(product);
          }}
          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-primary-50"
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50 p-1">
            {product.image ? (
              <img
                src={buildAssetUrl(product.image)}
                alt={name}
                className="h-full w-full object-contain"
              />
            ) : (
              <svg
                className="h-6 w-6 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight text-gray-900">
              {name}
            </p>
            {localizedNames.length > 0 && (
              <p className="mt-0.5 truncate text-xs text-gray-500">
                {localizedNames.join(" | ")}
              </p>
            )}
            {product.category_name && (
              <p className="truncate text-xs text-gray-500">
                {product.category_name}
              </p>
            )}
          </div>
          {Number(product.price) > 0 && (
            <span className="shrink-0 text-sm font-bold text-primary-600">
              Rs {parseFloat(product.price).toFixed(0)}
            </span>
          )}
        </button>
      );
    })}
    <button
      onMouseDown={(e) => {
        e.preventDefault();
        onViewAll();
      }}
      className="flex w-full items-center justify-center gap-2 border-t border-gray-100 px-4 py-3 text-sm font-semibold text-primary-600 hover:bg-primary-50"
    >
      View all results for "{searchQuery}"
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
    </button>
  </>
);

const SearchLanding = ({ data, loading, onChipClick, onClearHistory, onProductSelect }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }
  if (!data) return <div className="px-4 py-6 text-center text-sm text-gray-400">Start typing to search…</div>;

  const { recent_searches = [], trending_searches = [], suggested_products = [] } = data;
  const hasRecent = recent_searches.length > 0;
  const hasTrending = trending_searches.length > 0;
  const hasSuggested = suggested_products.length > 0;

  if (!hasRecent && !hasTrending && !hasSuggested) {
    return <div className="px-4 py-6 text-center text-sm text-gray-400">Start typing to search…</div>;
  }

  return (
    <div>
      {/* Recent Searches */}
      {hasRecent && (
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Recent Searches</span>
            <button
              onMouseDown={(e) => { e.preventDefault(); onClearHistory(); }}
              className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {recent_searches.map((term, i) => (
              <button
                key={i}
                onMouseDown={(e) => { e.preventDefault(); onChipClick(term); }}
                className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              >
                <FaClock className="h-3 w-3 shrink-0 text-gray-400" />
                {term}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Trending Searches */}
      {hasTrending && (
        <div className={`px-4 ${hasRecent ? "border-t border-gray-100 pt-3" : "pt-3"} pb-2`}>
          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-400">Trending</span>
          <div className="flex flex-wrap gap-2">
            {trending_searches.map((term, i) => (
              <button
                key={i}
                onMouseDown={(e) => { e.preventDefault(); onChipClick(term); }}
                className="flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs text-orange-700 transition-colors hover:border-orange-400 hover:bg-orange-100"
              >
                <FaFire className="h-3 w-3 shrink-0 text-orange-400" />
                {term}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Suggested Products */}
      {hasSuggested && (
        <div className={`${hasRecent || hasTrending ? "border-t border-gray-100" : ""} pt-2 pb-1`}>
          <span className="mb-1 block px-4 text-xs font-bold uppercase tracking-wider text-gray-400">Suggested</span>
          {suggested_products.map((product) => {
            const id = getProductId(product);
            const name = getProductName(product);
            return (
              <button
                key={id}
                onMouseDown={(e) => { e.preventDefault(); onProductSelect(product); }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-primary-50"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-50 p-1">
                  {product.image ? (
                    <img src={product.image} alt={name} className="h-full w-full object-contain" />
                  ) : (
                    <svg className="h-5 w-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{name}</p>
                  {product.variant_name && <p className="truncate text-xs text-gray-500">{product.variant_name}</p>}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-bold text-primary-600">Rs {parseFloat(product.price || 0).toFixed(0)}</p>
                  {product.mrp && Number(product.mrp) > Number(product.price) && (
                    <p className="text-xs text-gray-400 line-through">Rs {parseFloat(product.mrp).toFixed(0)}</p>
                  )}
                  {product.discount_percentage && Number(product.discount_percentage) > 0 && (
                    <p className="text-xs font-semibold text-green-600">{product.discount_percentage}% off</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SearchBar = () => {
  const navigate = useNavigate();
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    searchProducts,
    clearSearch,
    fetchSearchLanding,
    saveSearchHistory,
    clearSearchHistory,
    searchLandingData,
    searchLandingLoading,
    isAuthenticated,
  } = useStore();

  const [open, setOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open && searchQuery.trim().length === 0 && isAuthenticated) {
      fetchSearchLanding();
    }
  }, [open, searchQuery, fetchSearchLanding, isAuthenticated]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (searchQuery.trim().length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchProducts(searchQuery);
        setOpen(true);
      }, 400);
    } else {
      // query too short — don't close; landing panel takes over
    }
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, searchProducts]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 639px)");
    const syncViewport = () => setIsMobileViewport(query.matches);
    syncViewport();
    query.addEventListener("change", syncViewport);
    return () => query.removeEventListener("change", syncViewport);
  }, []);

  const handleSelect = (product) => {
    const productPath = getProductPath(product);
    if (!productPath) return;
    saveSearchHistory(getProductName(product));
    setOpen(false);
    navigate(productPath);
    setTimeout(() => clearSearch(), 100);
  };

  /* Chip click — fill input with term, trigger search, save to history */
  const handleChipClick = (term) => {
    setSearchQuery(term);
    searchProducts(term);
    saveSearchHistory(term);
  };

  const handleViewAll = () => {
    const q = searchQuery.trim();
    saveSearchHistory(q);
    setOpen(false);
    navigate(`/products?q=${encodeURIComponent(q)}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      saveSearchHistory(q);
      setOpen(false);
      navigate(`/products?q=${encodeURIComponent(q)}`);
    }
  };

  const hasResults = open && searchQuery.trim().length >= 2 && searchResults.length > 0;
  const isLanding = open && searchQuery.trim().length < 2;
  const noResults = open && searchQuery.trim().length >= 2 && !searchLoading && searchResults.length === 0;
  const dropdownVisible = hasResults || isLanding || noResults;

  useBodyScrollLock(dropdownVisible && isMobileViewport);

  return (
    <>
      <div ref={wrapperRef} className="relative w-full flex-1">
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              type="text"
              placeholder="Search vegetables, fruits, dairy... | भाज्या, फळे, दुग्धजन्य पदार्थ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setOpen(true)}
              className="w-full rounded-2xl border-2 border-gray-200 px-4 py-2.5 pr-12 text-sm shadow-sm transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-100 sm:py-3 sm:text-base"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => { clearSearch(); setOpen(false); }}
                className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600">
              {searchLoading
                ? <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
                : <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              }
            </button>
          </div>
        </form>

        {/* Desktop dropdown */}
        {dropdownVisible && (
          <div className="absolute left-0 right-0 top-full z-50 mt-2 hidden max-h-96 overflow-y-auto rounded-2xl border border-gray-100 bg-white shadow-2xl sm:block">
            {hasResults ? (
              <ResultList results={searchResults} searchQuery={searchQuery} onSelect={handleSelect} onViewAll={handleViewAll} />
            ) : isLanding ? (
              <SearchLanding
                data={searchLandingData}
                loading={searchLandingLoading}
                onChipClick={handleChipClick}
                onClearHistory={clearSearchHistory}
                onProductSelect={handleSelect}
              />
            ) : (
              <p className="px-4 py-6 text-center text-sm text-gray-500">
                No results for "<span className="font-semibold">{searchQuery}</span>"
              </p>
            )}
          </div>
        )}
      </div>

      {/* Mobile full-screen overlay */}
      {dropdownVisible && (
        <div className="fixed inset-0 z-[80] flex flex-col overflow-hidden bg-white sm:hidden">
          <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
            <form onSubmit={handleSubmit} className="flex-1">
              <div className="relative">
                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search vegetables, fruits... | भाज्या, फळे... | सब्जियां, फल..."
                  className="w-full rounded-xl border-2 border-primary-500 px-4 py-2.5 pr-10 text-sm focus:outline-none"
                />
                {searchQuery && (
                  <button type="button" onClick={() => clearSearch()} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </form>
            <button
              onClick={() => { setOpen(false); clearSearch(); }}
              className="shrink-0 rounded-xl bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {searchLoading ? (
              <div className="flex items-center justify-center py-16">
                <svg className="h-8 w-8 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              </div>
            ) : hasResults ? (
              <ResultList results={searchResults} searchQuery={searchQuery} onSelect={handleSelect} onViewAll={handleViewAll} />
            ) : isLanding ? (
              <SearchLanding
                data={searchLandingData}
                loading={searchLandingLoading}
                onChipClick={handleChipClick}
                onClearHistory={clearSearchHistory}
                onProductSelect={handleSelect}
              />
            ) : (
              <div className="px-4 py-12 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-500">
                  <FaSearch className="h-6 w-6" aria-hidden="true" />
                </div>
                <p className="text-sm text-gray-500">
                  No results for "<span className="font-semibold">{searchQuery}</span>"
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SearchBar;
